import React, { useState, useEffect, useRef, useContext } from "react";

import { useNavigate, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { makeStyles } from "@mui/styles";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import Badge from "@mui/material/Badge";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ImageIcon from "@mui/icons-material/Image";
import VideocamIcon from "@mui/icons-material/Videocam";
import MicIcon from "@mui/icons-material/Mic";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import IconButton from "@mui/material/IconButton";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import Tooltip from "@mui/material/Tooltip";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import MessagesList from "../MessagesList";
import TicketActionButtons from "../TicketActionButtons";
import TransferTicketModal from "../TransferTicketModal";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";

const useStyles = makeStyles(theme => ({
	ticket: {
		position: "relative",
		padding: "0 !important",
		height: 72,
		overflow: "hidden",
		backgroundColor: theme.palette.mode === 'dark' ? '#111b21' : '#fff',
		"&:hover": {
			backgroundColor: theme.palette.mode === 'dark' ? '#2a3942' : '#f5f6f6',
		},
		"&.Mui-selected": {
			backgroundColor: theme.palette.mode === 'dark' ? '#2a3942' : '#f0f2f5',
			"&:hover": {
				backgroundColor: theme.palette.mode === 'dark' ? '#2a3942' : '#f0f2f5',
			},
		},
	},

	pendingTicket: {
		cursor: "unset",
	},

	notPendingTicket: {
		paddingRight: "0px !important",
	},

	contactNameWrapper: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		gap: 6,
	},

	metaRight: {
		display: "flex",
		alignItems: "center",
		justifyContent: "flex-end",
		gap: 4,
		minWidth: 44,
		flexShrink: 0,
	},

	metaRightStack: {
		display: "flex",
		flexDirection: "column",
		alignItems: "flex-end",
		justifyContent: "center",
		gap: 4,
		minWidth: 44,
		flexShrink: 0,
	},

	metaInlineRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "flex-end",
		gap: 4,
		minHeight: 20,
	},

	lastMessageTime: {
		color: "#667781",
		fontSize: "0.75rem",
		fontWeight: 400,
	},

	closedBadge: {
		alignSelf: "center",
		justifySelf: "flex-end",
		marginRight: 32,
		marginLeft: "auto",
	},

	contactLastMessage: {
		paddingRight: 10,
		fontSize: "0.8rem",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
		color: theme.palette.mode === 'dark' ? '#a0a0a0' : '#667781',
	},

	previewCodeToken: {
		fontFamily: "monospace",
		fontSize: "0.78rem",
	},

	badgeStyle: {
		color: "white",
		backgroundColor: "#00a884",
	},

	acceptButton: {
		position: "absolute",
		right: 0,
		top: 0,
		bottom: 0,
		width: 56,
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "center",
		padding: "0 8px",
		backgroundColor: theme.palette.primary.main,
		color: "white",
		zIndex: 10,
		cursor: "pointer",
		overflow: "hidden",
		transition: "background-color 0.2s, width 0.2s ease",
		borderRadius: 0,
		boxShadow: "none",
		fontWeight: 700,
		"&:hover": {
			backgroundColor: theme.palette.primary.dark,
			width: 112,
			justifyContent: "flex-start",
			paddingLeft: 12,
		},
		"&:hover $acceptButtonLabel": {
			opacity: 1,
			maxWidth: 64,
			marginLeft: 6,
		},
	},

	acceptButtonLabel: {
		opacity: 0,
		maxWidth: 0,
		marginLeft: 0,
		overflow: "hidden",
		whiteSpace: "nowrap",
		fontSize: "0.72rem",
		fontWeight: 700,
		letterSpacing: "0.02em",
		transition: "opacity 0.16s ease, max-width 0.2s ease, margin-left 0.2s ease",
	},

	ticketQueueColor: {
		flex: "none",
		width: "4px",
		height: "100%",
		position: "absolute",
		top: "0%",
		left: 0,
	},

	userTag: {
		background: "#2576D2",
		color: "#ffffff",
		border: "1px solid #CCC",
		padding: "0 4px",
		borderRadius: 10,
		fontSize: "0.8em",
		marginLeft: 5
	},

	agentBadge: {
		"& .MuiBadge-badge": {
			right: 2,
			bottom: 2,
			width: 18,
			height: 18,
			borderRadius: "50%",
			backgroundColor: theme.palette.mode === "dark" ? "#202c33" : "#e9edef",
			color: theme.palette.mode === "dark" ? "#d1d7db" : "#3b4a54",
			border: `1px solid ${theme.palette.mode === "dark" ? "#2a3942" : "#d1d7db"}`,
			fontSize: "0.62rem",
			fontWeight: 700,
			lineHeight: 1,
			padding: 0,
		}
	},

	lineIndicator: {
		width: 18,
		height: 18,
		borderRadius: "50%",
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		fontSize: "0.62rem",
		fontWeight: 700,
		backgroundColor: theme.palette.mode === "dark" ? "#202c33" : "#e9edef",
		color: theme.palette.mode === "dark" ? "#d1d7db" : "#3b4a54",
		border: `1px solid ${theme.palette.mode === "dark" ? "#2a3942" : "#d1d7db"}`,
	},

	statusIndicator: {
		width: 18,
		height: 18,
		borderRadius: "50%",
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		border: `1px solid ${theme.palette.mode === "dark" ? "#2a3942" : "#d1d7db"}`,
		backgroundColor: theme.palette.mode === "dark" ? "#202c33" : "#e9edef",
		"& svg": {
			fontSize: "0.82rem",
		},
	},

	statusPending: {
		color: "#d32f2f",
	},

	statusOpen: {
		color: "#00a884",
	},

	statusClosed: {
		color: "#667781",
	},

	newMessagesCountCustom: {
		backgroundColor: "#00a884",
		color: "white",
		padding: "2px 6px",
		borderRadius: "10px",
		fontSize: "0.75rem",
		fontWeight: "bold",
		minWidth: "20px",
		height: "20px",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},

	listItemContent: {
		margin: "0 !important",
		paddingRight: 8,
		transition: "padding-right 0.2s ease",
	},

	pendingContent: {
		paddingRight: 64,
	},

	pendingContentExpanded: {
		paddingRight: 120,
	},

	previewHeader: {
		display: "flex",
		alignItems: "center",
		backgroundColor: theme.palette.mode === "dark" ? "#111b21" : "#f0f2f5",
		minHeight: 56,
		padding: theme.spacing(0.75, 1.25),
		gap: theme.spacing(1),
	},

	previewAvatar: {
		width: 40,
		height: 40,
	},

	previewInfo: {
		flex: 1,
		minWidth: 0,
	},

	previewName: {
		fontSize: "1rem",
		fontWeight: 600,
		lineHeight: 1.2,
	},

	previewQueue: {
		fontSize: "0.75rem",
		color: theme.palette.mode === "dark" ? "#aebac1" : "#667781",
		display: "flex",
		alignItems: "center",
		gap: 6,
		marginTop: 2,
	},

	previewQueueDot: {
		width: 8,
		height: 8,
		borderRadius: "50%",
		flex: "none",
	},

	mediaPreview: {
		display: "inline-flex",
		alignItems: "center",
		gap: 4,
		fontWeight: 500,
	},

	mediaPreviewIcon: {
		fontSize: "0.95rem !important",
	},
}));

const IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "heic", "heif"]);
const VIDEO_EXTENSIONS = new Set(["mp4", "mov", "mkv", "avi", "webm", "3gp", "m4v"]);
const AUDIO_EXTENSIONS = new Set(["mp3", "ogg", "opus", "wav", "m4a", "aac", "flac", "amr"]);

const TicketListItem = ({ ticket }) => {
	const classes = useStyles();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [isAcceptHovered, setIsAcceptHovered] = useState(false);
	const [openPreview, setOpenPreview] = useState(false);
	const [openTransferModal, setOpenTransferModal] = useState(false);
	const { ticketId } = useParams();
	const ticketPathId = ticket.publicId || ticket.id;
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);

	const getMediaPreviewType = (message) => {
		if (!message) return null;

		const normalized = String(message)
			.replace(/^[\u200B-\u200D\u2060\uFEFF\u00A0]+/u, "")
			.trim()
			.toLowerCase();
		if (!normalized) return null;

		const mediaPlaceholders = {
			"[image]": "image",
			"[imagen]": "image",
			"[video]": "video",
			"[audio]": "audio",
			"[ptt]": "voice",
			"[voice]": "voice",
			"[nota de voz]": "voice",
		};

		if (mediaPlaceholders[normalized]) {
			return mediaPlaceholders[normalized];
		}

		if (normalized.startsWith("image-")) return "image";
		if (normalized.startsWith("video-")) return "video";
		if (normalized.startsWith("audio-")) return "audio";
		if (normalized.startsWith("ptt-")) return "voice";

		const cleanSegment = normalized.split("/").pop()?.split("?")[0]?.split("#")[0] || "";
		if (!cleanSegment.includes(".")) return null;

		const ext = cleanSegment.split(".").pop();
		if (!ext) return null;

		if (IMAGE_EXTENSIONS.has(ext)) return "image";
		if (VIDEO_EXTENSIONS.has(ext)) return "video";
		if (AUDIO_EXTENSIONS.has(ext)) return normalized.includes("ptt") ? "voice" : "audio";

		return null;
	};

	const renderFormattedPreview = (message) => {
		if (!message) return null;

		const mediaType = getMediaPreviewType(message);
		if (mediaType === "image") {
			return (
				<span className={classes.mediaPreview}>
					<ImageIcon className={classes.mediaPreviewIcon} />
					Imagen
				</span>
			);
		}

		if (mediaType === "video") {
			return (
				<span className={classes.mediaPreview}>
					<VideocamIcon className={classes.mediaPreviewIcon} />
					Video
				</span>
			);
		}

		if (mediaType === "audio") {
			return (
				<span className={classes.mediaPreview}>
					<AudiotrackIcon className={classes.mediaPreviewIcon} />
					Audio
				</span>
			);
		}

		if (mediaType === "voice") {
			return (
				<span className={classes.mediaPreview}>
					<MicIcon className={classes.mediaPreviewIcon} />
					Nota de voz
				</span>
			);
		}

		const singleLine = String(message).replace(/\s*\n+\s*/g, " ").trim();
		const truncated =
			singleLine.length > 80 ? `${singleLine.slice(0, 80).trimEnd()}...` : singleLine;

		const pattern = /(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~|`[^`\n]+`)/g;
		const parts = [];
		let lastIndex = 0;
		let tokenIndex = 0;
		let match;

		while ((match = pattern.exec(truncated)) !== null) {
			const fullMatch = match[0];
			const start = match.index;

			if (start > lastIndex) {
				parts.push(truncated.slice(lastIndex, start));
			}

			const marker = fullMatch.charAt(0);
			const content = fullMatch.slice(1, -1);
			const key = `preview-token-${tokenIndex}`;

			if (marker === "*") {
				parts.push(<strong key={key}>{content}</strong>);
			} else if (marker === "_") {
				parts.push(<em key={key}>{content}</em>);
			} else if (marker === "~") {
				parts.push(<s key={key}>{content}</s>);
			} else if (marker === "`") {
				parts.push(
					<span key={key} className={classes.previewCodeToken}>
						{content}
					</span>
				);
			}

			lastIndex = start + fullMatch.length;
			tokenIndex += 1;
		}

		if (lastIndex < truncated.length) {
			parts.push(truncated.slice(lastIndex));
		}

		return parts.length ? parts : truncated;
	};

	const getUserInitial = (name) => {
		if (!name || typeof name !== "string") return "?";
		return name.trim().charAt(0).toUpperCase();
	};

	const getWhatsappInitial = (name) => {
		if (!name || typeof name !== "string") return "?";
		return name.trim().charAt(0).toUpperCase();
	};

	const getStatusMeta = () => {
		if (ticket?.status === "pending") {
			return null;
		}

		if (ticket?.status === "open") {
			return {
				className: classes.statusOpen,
				icon: <SupportAgentIcon />,
				label: i18n.t("ticketListItem.status.open"),
			};
		}

		if (ticket?.status === "closed") {
			return {
				className: classes.statusClosed,
				icon: <TaskAltIcon />,
				label: i18n.t("ticketListItem.status.closed"),
			};
		}

		return null;
	};

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const handleAcepptTicket = async id => {
		setLoading(true);
		try {
			await api.put(`/tickets/${id}`, {
				status: "open",
				userId: user?.id,
			});
			// Navigate regardless of isMounted - the socket 'delete' event
			// unmounts this component before the await resolves, so
			// isMounted.current is already false here. history works fine
			// even after unmount.
			navigate(`/tickets/${id}`, { state: { forceTab: "open" } });
		} catch (err) {
			toastError(err);
			if (isMounted.current) setLoading(false);
		}
	};

	const handleSelectTicket = (t) => {
		if (t.status === "pending") return;
		navigate(`/tickets/${t.publicId || t.id}`);
	};

	const handleTransferredFromPreview = () => {
		setOpenTransferModal(false);
		setOpenPreview(false);
	};

	const statusMeta = getStatusMeta();
	const avatarBadgeContent =
		ticket?.status === "open" && ticket?.user?.name
			? getUserInitial(ticket.user.name)
			: null;
	const avatarWithBadge = (
		<Badge
			overlap="circular"
			className={classes.agentBadge}
			anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
			badgeContent={avatarBadgeContent}
		>
			<Avatar
				src={ticket?.contact?.profilePicUrl}
				sx={{
					width: 48,
					height: 48,
				}}
			/>
		</Badge>
	);

	return (
		<React.Fragment key={ticket.id}>
			<ListItemButton
				dense
				disableGutters
				onClick={() => handleSelectTicket(ticket)}
				selected={ticketId && ticketId === String(ticketPathId)}
				className={clsx(classes.ticket, {
					[classes.pendingTicket]: ticket.status === "pending",
				})}
			>
				<Tooltip
					arrow
					placement="right"
					title={`${i18n.t("ticketListItem.tooltips.queue")}: ${ticket?.queue?.name || i18n.t("ticketListItem.noQueue")}`}
				>
					<span
						style={{ backgroundColor: ticket?.queue?.color || "#7C7C7C" }}
						className={classes.ticketQueueColor}
					></span>
				</Tooltip>
				<ListItemAvatar style={{ paddingLeft: 8, minWidth: 64 }}>
					{ticket?.user?.name ? (
						<Tooltip title={`${i18n.t("ticketListItem.tooltips.agent")}: ${ticket.user.name}`}>
							{avatarWithBadge}
						</Tooltip>
					) : (
						avatarWithBadge
					)}
				</ListItemAvatar>
				<ListItemText
					disableTypography
					className={clsx(classes.listItemContent, {
						[classes.pendingContent]: ticket?.status === "pending",
						[classes.pendingContentExpanded]:
							ticket?.status === "pending" && isAcceptHovered,
					})}
					primary={
						<span className={classes.contactNameWrapper}>
							<Typography
								noWrap
								component="span"
								variant="body2"
								color="textPrimary"
								style={{ flex: 1, fontWeight: ticket?.unreadMessages > 0 ? "bold" : "normal", fontSize: "0.85rem" }}
							>
								{ticket?.contact?.name}
							</Typography>
							<span className={classes.metaRight}>
								<Typography
									className={classes.lastMessageTime}
									component="span"
									variant="body2"
								>
									{ticket?.updatedAt && (isSameDay(parseISO(ticket.updatedAt), new Date())
										? format(parseISO(ticket.updatedAt), "HH:mm")
										: format(parseISO(ticket.updatedAt), "dd/MM/yyyy"))}
								</Typography>
							</span>
						</span>
					}
					secondary={
						<span className={classes.contactNameWrapper}>
							<Typography
								className={classes.contactLastMessage}
								noWrap
								component="span"
								variant="body2"
								color="textSecondary"
								style={{ flex: 1 }}
							>
								{ticket?.lastMessage ? (
									renderFormattedPreview(ticket.lastMessage)
								) : (
									<br />
								)}
							</Typography>
							<span className={classes.metaRightStack}>
								<span className={classes.metaInlineRow}>
									{statusMeta && (
										<Tooltip title={statusMeta.label}>
											<span className={clsx(classes.statusIndicator, statusMeta.className)}>
												{statusMeta.icon}
											</span>
										</Tooltip>
									)}
									{ticket?.status === "pending" && (
										<Tooltip title="Preview">
											<VisibilityIcon
												onClick={(e) => { e.stopPropagation(); setOpenPreview(true); }}
												style={{ color: "gray", cursor: "pointer", fontSize: '1.1rem' }}
											/>
										</Tooltip>
									)}
									{ticket?.unreadMessages > 0 && (
										<div className={classes.newMessagesCountCustom}>
											{ticket.unreadMessages}
										</div>
									)}
								</span>
								{ticket?.whatsapp?.name ? (
									<Tooltip title={`${i18n.t("ticketListItem.tooltips.line")}: ${ticket.whatsapp.name}`}>
										<span className={classes.lineIndicator}>
											{getWhatsappInitial(ticket.whatsapp.name)}
										</span>
									</Tooltip>
								) : null}
							</span>
						</span>
					}
				/>

				{ticket.status === "pending" && (
					<div
						className={classes.acceptButton}
						title={i18n.t("ticketsList.buttons.accept")}
						onMouseEnter={() => setIsAcceptHovered(true)}
						onMouseLeave={() => setIsAcceptHovered(false)}
						onClick={e => {
							e.stopPropagation();
						handleAcepptTicket(ticketPathId);
						}}
					>
						<CheckCircleIcon sx={{ fontSize: "1.2rem" }} />
						<span className={classes.acceptButtonLabel}>
							{i18n.t("ticketsList.buttons.accept")}
						</span>
					</div>
				)}
			</ListItemButton>
			<Dialog
				open={openPreview}
				onClose={() => setOpenPreview(false)}
				fullWidth
				maxWidth="sm"
			>
				<DialogTitle id="form-dialog-title" style={{ padding: 0 }}>
					<div className={classes.previewHeader}>
						<Avatar
							src={ticket?.contact?.profilePicUrl}
							className={classes.previewAvatar}
						/>
						<div className={classes.previewInfo}>
							<Typography noWrap className={classes.previewName}>
								{ticket?.contact?.name}
							</Typography>
							<Box className={classes.previewQueue}>
								<span
									className={classes.previewQueueDot}
									style={{ backgroundColor: ticket?.queue?.color || "#7C7C7C" }}
								/>
								<span>{ticket?.queue?.name || "Sin departamento"}</span>
							</Box>
						</div>
						<div style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: "auto" }}>
							{ticket.status === "pending" && (
								<Tooltip title={i18n.t("messagesList.header.buttons.transfer")}>
									<IconButton
										size="small"
										onClick={() => setOpenTransferModal(true)}
										sx={{ color: "#1976d2" }}
									>
										<SyncAltIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							)}
							<TicketActionButtons
								ticket={ticket}
								variant="compact"
								allowResolvePending
							/>
							<Tooltip title="Cerrar">
								<IconButton onClick={() => setOpenPreview(false)} size="small">
									<CloseIcon />
								</IconButton>
							</Tooltip>
						</div>
					</div>
				</DialogTitle>
				<DialogContent dividers style={{ height: '600px', display: 'flex', flexDirection: 'column', padding: 0 }}>
					<ReplyMessageProvider>
						<MessagesList ticketId={ticketPathId} preview={true} />
					</ReplyMessageProvider>
				</DialogContent>
			</Dialog>
			<TransferTicketModal
				modalOpen={openTransferModal}
				onClose={() => setOpenTransferModal(false)}
				ticketid={ticketPathId}
				ticketWhatsappId={ticket.whatsappId}
				hideUserSelection
				useWildcardUser
				skipNavigate
				onTransferred={handleTransferredFromPreview}
			/>
			<Divider variant="inset" component="li" />
		</React.Fragment>
	);
};

export default TicketListItem;
